import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Typography } from 'components/App/GlobalStyled';
import { RPagination } from 'components/RPagination';
import { RLoader } from 'components/RLoader';
import { RMask } from 'components/RMask';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { SortingArrow } from './SortingArrow';
import {
  EmptyText,
  LoadingStyled,
  PaginationStyled,
  ScrollContainer,
  TBody,
  TCell,
  THead,
  THeaderAction,
  TRow,
  Wrap,
} from './styled';
import { PLUS_SVG } from '../ui/_shared/SVG';
import { RButton } from '../ui/RButton';

export const TableRefContext = createContext(null);

export function RTable({
  columns = [],
  onRowClick,
  navigationRoute,
  navigationContext,
  data,
  rowsCount = 18,
  height = 510,
  cursiveRowField,
  listEmptyMessage,
  currentPage = 1,
  totalItems,
  disablePagination,
  onPageChange,
  loading,
  onBackendSortChange,
  isNotes,
  toggleModal,
  headerAction,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);
  const shouldHandleRowClick = onRowClick || navigationRoute;
  const theme = useTheme();

  const [sorting, setSorting] = useState({
    column: '',
    sortFunction: null,
    isAsc: true,
    backendSortSlug: null,
  });

  const onSetSorting = (data) => {
    if (onBackendSortChange) {
      onBackendSortChange(data);
    }

    setSorting(data);
  };

  const handleHeaderClick = (cell) => () => {
    if (!cell.sortFunction && !cell.backendSortSlug) return;

    const isCurrentColumn = sorting.column === cell.slug;
    const newIsAsc = isCurrentColumn ? !sorting.isAsc : true;

    onSetSorting({
      column: cell.slug,
      sortFunction: cell.sortFunction,
      isAsc: newIsAsc,
      backendSortSlug: cell.backendSortSlug,
    });
  };

  const sortedData = sorting.sortFunction
    ? [...data].sort((a, b) => sorting.sortFunction(a, b, sorting.isAsc))
    : data;

  const displayedData = !onBackendSortChange
    ? sortedData.slice((currentPage - 1) * rowsCount, currentPage * rowsCount)
    : sortedData;

  const contextValue = useMemo(
    () => ({ tableRef, tableWidth }),
    [tableRef, tableWidth]
  );

  const handleRowClick = (row) => () => {
    if (!shouldHandleRowClick) return;

    if (navigationRoute) {
      const id = !isNotes ? row.id : row.updated_by.id;
      navigate(navigationRoute.replace(':id', id), {
        state: {
          referrer: location.pathname,
          referrerContext: navigationContext,
        },
      });
      return;
    }

    onRowClick(row);
  };

  useEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.offsetWidth);
    }
  }, []);

  return (
    <TableRefContext.Provider value={contextValue}>
      <Wrap ref={tableRef}>
        {loading && (
          <RMask css={{ borderRadius: '12px', zIndex: 1 }}>
            <LoadingStyled>
              <RLoader />
            </LoadingStyled>
          </RMask>
        )}
        <ScrollContainer noScroll={!displayedData.length}>
          <THead>
            <TRow>
              {columns.map((cell) => (
                <TCell
                  key={`head-${cell.slug}`}
                  css={{
                    ...cell.style,
                    ...cell.headerStyle,
                    cursor:
                      cell.sortFunction || cell.backendSortSlug
                        ? 'pointer'
                        : 'default',
                  }}
                  onClick={handleHeaderClick(cell)}
                >
                  {cell.title}
                  {(cell.sortFunction || cell.backendSortSlug) && (
                    <SortingArrow
                      column={cell.slug}
                      isSelected={sorting.column === cell.slug}
                      isAsc={sorting.isAsc}
                    />
                  )}
                </TCell>
              ))}
              {headerAction && <THeaderAction>{headerAction}</THeaderAction>}
            </TRow>
          </THead>

          <TBody>
            {displayedData.map((row) => (
              <TRow
                key={`row-${row.id}`}
                onClick={handleRowClick(row)}
                isClickable={shouldHandleRowClick}
                isCursive={cursiveRowField && row[cursiveRowField]}
                css={row.style}
              >
                {columns.map((cell) => (
                  <TCell
                    key={`body-${cell.slug}`}
                    css={cell.style}
                  >
                    {cell.render ? cell.render(row) : row[cell.slug]}
                  </TCell>
                ))}
              </TRow>
            ))}

            {!displayedData.length ? (
              <EmptyText>{!loading && listEmptyMessage}</EmptyText>
            ) : null}
          </TBody>
        </ScrollContainer>

        {!disablePagination && (
          <PaginationStyled
            style={{
              margin: isNotes ? '6px 0' : '0',
              justifyContent: isNotes ? 'end' : 'space-between',
            }}
          >
            {!isNotes && (
              <Typography color="#344054">
                {!!data?.length && (
                  <>
                    Showing data {(currentPage - 1) * rowsCount + 1} to&nbsp;
                    {Math.min(
                      currentPage * rowsCount,
                      totalItems || data.length
                    )}{' '}
                    of&nbsp;
                  </>
                )}
                {totalItems || data.length} entries
              </Typography>
            )}

            {!isNotes && (
              <RPagination
                current={currentPage}
                total={totalItems || data.length}
                perPage={rowsCount}
                onChange={onPageChange}
              />
            )}
            {isNotes && (
              <RButton
                style={{ width: '192px', marginRight: '24px' }}
                prependIcon={
                  <PLUS_SVG
                    size={18}
                    color={theme.gray25}
                  />
                }
                fullWidth
                onClick={toggleModal}
              >
                Add Comment
              </RButton>
            )}
          </PaginationStyled>
        )}
      </Wrap>
    </TableRefContext.Provider>
  );
}

RTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      style: PropTypes.any,
      headerStyle: PropTypes.any,
      render: PropTypes.func,
      sortFunction: PropTypes.func,
    })
  ).isRequired,
  rowsCount: PropTypes.number,
  listEmptyMessage: PropTypes.string,
  onRowClick: PropTypes.func,
  navigationRoute: PropTypes.string,
  navigationContext: PropTypes.shape({
    entityType: PropTypes.string,
    entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entityName: PropTypes.string,
  }),
  height: PropTypes.number,
  cursiveRowField: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentPage: PropTypes.number,
  disablePagination: PropTypes.bool,
  onPageChange: PropTypes.func,
  totalItems: PropTypes.number,
  loading: PropTypes.bool,
  onBackendSortChange: PropTypes.func,
  isNotes: PropTypes.bool,
  toggleModal: PropTypes.func,
  headerAction: PropTypes.node,
};
